import React, { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

interface ConfirmActionModalProps {
  isOpen: boolean;
  title: string;
  message?: React.ReactNode;
  content?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  isConfirmDisabled?: boolean;
  isCancelDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Reusable confirmation modal for destructive actions
 * @param {*} param0
 * @returns
 */
const ConfirmActionModal = ({
  isOpen,
  title,
  message,
  content = null,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  isConfirmDisabled = false,
  isCancelDisabled = false,
  onConfirm,
  onCancel,
}: ConfirmActionModalProps) => {
  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);
  const messages = Array.isArray(message) ? message : [message];

  useEffect(() => {
    if (!isOpen) return undefined;

    cancelButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/40 px-4 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-action-title"
        aria-describedby="confirm-action-message"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-xl rounded-3xl border border-indigo-100 bg-white p-8 shadow-2xl shadow-slate-300/40 animate-[fadeIn_.18s_ease-out]"
      >
        <div className="flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 text-red-600">
            <AlertTriangle size={24} />
          </div>

          <div className="mt-4 w-full">
            <h3 id="confirm-action-title" className="text-xl font-semibold text-slate-900">
              {title}
            </h3>
            {content ? (
              <div id="confirm-action-message" className="mt-5">
                {content}
              </div>
            ) : messages.length === 1 ? (
              <p id="confirm-action-message" className="mt-5 text-sm leading-6 text-slate-600">
                {messages[0]}
              </p>
            ) : (
              <ul
                id="confirm-action-message"
                className="mt-5 inline-block list-disc space-y-2 pl-5 text-left text-sm leading-6 text-slate-600"
              >
                {messages.map((item, index) => (
                  <li key={`${title}-message-${index}`}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            disabled={isCancelDisabled}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isConfirmDisabled}
            className="rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmActionModal;
